import {
  type ColDef,
  type RowSelectionOptions,
  type SelectionChangedEvent,
} from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { useMemo } from 'react'
import type { QuestionSummary } from '../api/questions'
import { defaultColDef as sharedColDef, gridTheme } from '../grid'
import { londonTime } from './QuestionFeedback'

/**
 * Multi-row selection with the checkbox column AG Grid draws itself, including the
 * header box and its indeterminate state. `enableClickSelection: false` because a click
 * on the row opens the Question -- selecting and opening are different intents and the
 * same click cannot mean both.
 */
const rowSelection: RowSelectionOptions = {
  mode: 'multiRow',
  enableClickSelection: false,
}

/**
 * A page of rows, and what one costs in pixels.
 *
 * The grid has no height of its own -- it is as tall as its rows -- so the space a full
 * page needs is reserved up front. Without it the table is a few hundred pixels while the
 * fetch is in flight and several thousand a moment later, and everything below it jumps.
 */
const PAGE_SIZE = 100

const ROW_HEIGHT = 42

const HEADER_HEIGHT = 48

const FILTER_ROW_HEIGHT = 40

/** The pager AG Grid draws under the rows. */
const PAGER_HEIGHT = 48

const FULL_PAGE_HEIGHT =
  HEADER_HEIGHT + FILTER_ROW_HEIGHT + PAGE_SIZE * ROW_HEIGHT + PAGER_HEIGHT

export interface QuestionsGridProps {
  rows: QuestionSummary[]
  pending: boolean
  onSelectionChanged: (ids: number[]) => void
  /** A click anywhere else on the row opens that Question. */
  onOpen: (id: number) => void
}

export function QuestionsGrid({ rows, pending, onSelectionChanged, onOpen }: QuestionsGridProps) {
  // AG Grid draws the selection column itself; pinning it left keeps it ahead of the id,
  // which is also pinned, so the checkboxes stay at the far edge where they are looked for.
  const selectionColumnDef = useMemo<ColDef<QuestionSummary>>(
    () => ({ pinned: 'left', width: 52, resizable: false, suppressHeaderMenuButton: true }),
    [],
  )

  const columns = useMemo<ColDef<QuestionSummary>[]>(
    () => [
      {
        field: 'id',
        headerName: 'Id',
        // Six digits is the whole of it -- the Bank is ~1,100 Questions at launch (app:R31)
        // and an identity that never resets still has room to spare in a million.
        width: 78,
        pinned: 'left',
        cellClass: 'font-bold',
        // One column where there were two. The id is the Question's identity and its
        // place in the order at once, so it can never be hidden: a table of Questions
        // without it is a table nobody can point at a row of.
        lockVisible: true,
        filter: 'agNumberColumnFilter',
      },
      {
        // When a Student last wrote about the Question, so sorting it puts the latest
        // feedback first; what is still unread rides on it, two digits as every count here
        // is. Blank where nobody has written, and a blank sorts last either way.
        field: 'latestFeedbackAt',
        headerName: 'Feedback',
        width: 220,
        pinned: 'left',
        filter: 'agDateColumnFilter',
        filterValueGetter: (row) => (row.data?.latestFeedbackAt ? new Date(row.data.latestFeedbackAt) : null),
        valueFormatter: (cell) => {
          if (!cell.value) return ''
          const unread = cell.data?.unreadFeedback ?? 0
          const when = londonTime(cell.value as string)
          return unread > 0 ? `${when} · ${String(unread).padStart(2, '0')} new` : when
        },
        cellClass: (cell) => ((cell.data?.unreadFeedback ?? 0) > 0 ? 'font-bold text-green-800' : ''),
      },
      { field: 'kind', headerName: 'Type', width: 130 },
      { field: 'topicCode', headerName: 'Topic Code', width: 130 },
      { field: 'topic', headerName: 'Topic', width: 180 },
      { field: 'difficulty', headerName: 'Difficulty', width: 130 },
      { field: 'placement', headerName: 'Placement', width: 160 },
      { field: 'examOrder', headerName: 'Order', width: 100, filter: 'agNumberColumnFilter' },
      { field: 'optionCount', headerName: 'Options', width: 110, filter: 'agNumberColumnFilter' },
      {
        field: 'isComplete',
        headerName: 'Complete',
        width: 130,
        // Word, not a tick: colour alone cannot carry a distinction (WCAG 1.4.1), and a
        // boolean column filters as text far more usefully than as true/false.
        valueFormatter: (cell) => (cell.value ? 'Complete' : 'Partial'),
        cellClass: (cell) => (cell.value ? 'text-green-800' : 'text-muted'),
      },
      { field: 'mockCode', headerName: 'Mock', width: 120 },
      {
        field: 'tip',
        headerName: 'Tip',
        flex: 1,
        minWidth: 180,
        tooltipField: 'tip',
        valueFormatter: (cell) => cell.value ?? '',
      },
      {
        field: 'createdAt',
        headerName: 'Added',
        width: 170,
        filter: 'agDateColumnFilter',
        sort: 'desc',
        // The dates are ISO on the wire; en-GB here because everything a user sees is
        // British English on the Europe/London clock (platform:R02).
        valueFormatter: (cell) =>
          cell.value
            ? new Date(cell.value as string).toLocaleString('en-GB', {
                dateStyle: 'short',
                timeStyle: 'short',
              })
            : '',
        // The filter compares Dates, so the ISO string has to become one first.
        filterValueGetter: (row) => (row.data?.createdAt ? new Date(row.data.createdAt) : null),
      },
    ],
    [],
  )

  const defaultColDef = useMemo<ColDef<QuestionSummary>>(
    () => ({
      // The filter row under every heading, which is what was wanted: type a word and the
      // column narrows. AG Grid draws and runs it; nothing here reimplements one.
      ...sharedColDef,
    }),
    [],
  )

  return (
    // No fixed height, so the grid is as tall as its rows and never scrolls inside itself.
    // Pagination is what bounds it: a page is PAGE_SIZE rows, so that is the most it can
    // grow to before the pager takes over. `ponytail: autoHeight turns off row
    // virtualisation, which is fine for a page of 100 and would not be for a page of 5,000
    // -- cap the page-size selector rather than the height if that ever comes up.`
    //
    // The minimum is a whole page of rows, reserved before the data arrives, so the page
    // does not grow under the reader as it loads and take the scroll position with it.
    <div className="mt-6 w-full" style={{ minHeight: FULL_PAGE_HEIGHT }}>
      <AgGridReact<QuestionSummary>
        theme={gridTheme}
        columnDefs={columns}
        defaultColDef={defaultColDef}
        rowData={pending ? undefined : rows}
        loading={pending}
        // Guarded, not just stringified. A row whose id is missing -- which is what an
        // API serving an older contract looks like -- would otherwise give every row the
        // id "undefined", and AG Grid folds rows that share one into a single row: the
        // table silently shrinks to one, and every selection and double-click names it.
        // A per-row fallback keeps the rows apart so the empty column is the only symptom.
        getRowId={(row) => (row.data.id === undefined ? `missing-${row.data.createdAt}` : String(row.data.id))}
        rowSelection={rowSelection}
        selectionColumnDef={selectionColumnDef}
        onSelectionChanged={(event: SelectionChangedEvent<QuestionSummary>) =>
          onSelectionChanged(event.api.getSelectedRows().map((row) => row.id))
        }
        onRowDoubleClicked={(event) => {
          if (event.data) onOpen(event.data.id)
        }}
        domLayout="autoHeight"
        // Stated rather than inherited, because FULL_PAGE_HEIGHT is worked out from them:
        // a theme that changed a row's height would otherwise change how much space the
        // grid reserves without anything saying so.
        rowHeight={ROW_HEIGHT}
        headerHeight={HEADER_HEIGHT}
        floatingFiltersHeight={FILTER_ROW_HEIGHT}
        pagination
        paginationPageSize={PAGE_SIZE}
        paginationPageSizeSelector={[50, PAGE_SIZE, 200, 500]}
        overlayNoRowsTemplate="No Question matches those filters."
        tooltipShowDelay={400}
      />
    </div>
  )
}
